
import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { showSuccessToast, showErrorToast } from "@/utils/toast";

export function useAuthTokens() {
  const [isProcessing, setIsProcessing] = useState(false);
  const isClaimingRef = useRef(false);

  const redeemToken = useCallback(async (
    token: string,
    userId: string,
    memorialPurpose: 'self' | 'loved_one'
  ): Promise<{ success: boolean; message: string; quantity?: number; memorialId?: string }> => {
    try {
      if (!userId) {
        return { success: false, message: "You must be logged in to redeem a token" };
      }

      if (isClaimingRef.current) {
        return { success: false, message: "A token redemption is already in progress" };
      }

      isClaimingRef.current = true;
      setIsProcessing(true);

      // First get the token data
      const { data: tokenData, error: tokenError } = await supabase
        .from('memorial_tokens')
        .select('*')
        .eq('token', token)
        .single();

      if (tokenError || !tokenData) {
        console.error("Invalid token error:", tokenError);
        return { success: false, message: "Invalid or expired token" };
      }

      if (tokenData.claimed && tokenData.claimed_by !== userId) {
        return { success: false, message: "This token has already been claimed by another user" };
      }

      if (tokenData.claimed && tokenData.claimed_by === userId) {
        return {
          success: true,
          message: "You've already redeemed this token",
          memorialId: tokenData.memorial_id
        };
      }

      // Get the current user's email from auth session
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email;

      // Update token with claimed status AND email
      const { error: updateError } = await supabase
        .from('memorial_tokens')
        .update({
          claimed: true,
          claimed_by: userId,
          email: userEmail || null
        })
        .eq('id', tokenData.id);

      if (updateError) {
        console.error("Token redemption error:", updateError);
        return { success: false, message: "Failed to redeem token" };
      }

      // Transfer ownership of the linked memorial if it exists
      if (tokenData.memorial_id) {
        const { data: updateResult, error: transferError } = await supabase
          .from('memorials')
          .update({
            user_id: userId,
            is_active: true,
            memorial_purpose: memorialPurpose
          })
          .eq('id', tokenData.memorial_id)
          .select();

        if (transferError) {
          console.error("Error transferring memorial ownership:", transferError);
          console.error("Full error details:", JSON.stringify(transferError, null, 2));

          // If it's a constraint violation (invalid purpose value), fail the operation
          if (transferError.message?.includes('violates check constraint') ||
              transferError.message?.includes('memorial_purpose')) {
            return {
              success: false,
              message: "Invalid memorial purpose value. Please contact support."
            };
          }

          // For other errors, don't fail the whole operation
        }
      }

      // Verify the token was actually updated
      const { data: verifiedToken, error: verifyError } = await supabase
        .from('memorial_tokens')
        .select('*')
        .eq('id', tokenData.id)
        .single();

      if (verifyError || !verifiedToken || !verifiedToken.claimed || verifiedToken.claimed_by !== userId) {
        console.error("Token verification failed:", verifyError || "Token not properly claimed");
        return { success: false, message: "Token redemption could not be verified" };
      }

      return {
        success: true,
        message: `Successfully activated your memorial!`,
        memorialId: verifiedToken.memorial_id
      };
    } catch (error: unknown) {
      console.error("Token redemption error:", error);
      return { success: false, message: error instanceof Error ? error.message : "An error occurred during token redemption" };
    } finally {
      isClaimingRef.current = false;
      setIsProcessing(false);
    }
  }, []);

  const claimPendingTokensByEmail = useCallback(async (email: string, userId: string): Promise<{ success: boolean; claimed: number; hasPreCreatedMemorials?: boolean }> => {
    try {
      if (!email || !userId) {
        return { success: false, claimed: 0 };
      }

      if (isClaimingRef.current) {
        return { success: false, claimed: 0 };
      }

      isClaimingRef.current = true;
      setIsProcessing(true);

      const normalizedEmail = email.toLowerCase().trim();

      // First, check for B2B tokens by assigned_email (these are assigned by monument companies)
      const { data: b2bTokens, error: b2bError } = await supabase
        .from('memorial_tokens')
        .select('id, memorial_id, assigned_email')
        .eq('token_type', 'b2b')
        .ilike('assigned_email', normalizedEmail)
        .eq('claimed', false);

      if (b2bError) {
        console.error("Error fetching B2B tokens:", b2bError);
      } else if (b2bTokens && b2bTokens.length > 0) {
        // Process B2B tokens
        for (const token of b2bTokens) {
          // Update token as claimed
          const { error: updateError } = await supabase
            .from('memorial_tokens')
            .update({
              claimed: true,
              claimed_by: userId
            })
            .eq('id', token.id);

          if (updateError) {
            console.error(`Error claiming B2B token ${token.id}:`, updateError);
          }

          // Transfer memorial ownership
          if (token.memorial_id) {
            const { error: memorialError } = await supabase
              .from('memorials')
              .update({
                user_id: userId,
                is_active: true
              })
              .eq('id', token.memorial_id);

            if (memorialError) {
              console.error(`Error transferring B2B memorial ${token.memorial_id}:`, memorialError);
            }
          }
        }

        // Verify B2B memorials were transferred
        const b2bMemorialIds = b2bTokens.filter(t => t.memorial_id).map(t => t.memorial_id!);
        if (b2bMemorialIds.length > 0) {
          const { data: verifiedB2BMemorials } = await supabase
            .from('memorials')
            .select('id, user_id')
            .in('id', b2bMemorialIds);

          const b2bTransferred = verifiedB2BMemorials?.filter(m => m.user_id === userId).length || 0;

          if (b2bTransferred > 0) {
            return {
              success: true,
              claimed: b2bTokens.length,
              hasPreCreatedMemorials: true
            };
          }
        }
      }
      
      // Get all pending tokens for this email
      const { data: pendingTokens, error: tokensError } = await supabase
        .from('memorial_tokens')
        .select('id, email, memorial_id')
        .eq('email', normalizedEmail)
        .eq('claimed', false)
        .is('claimed_by', null);

      if (tokensError) {
        console.error("Error fetching pending tokens:", tokensError);
        return { success: false, claimed: 0 };
      }

      if (!pendingTokens || pendingTokens.length === 0) {
        // Fallback: Try case-insensitive search
        const { data: altTokens, error: altError } = await supabase
          .from('memorial_tokens')
          .select('id, email, memorial_id')
          .ilike('email', normalizedEmail)
          .eq('claimed', false)
          .is('claimed_by', null);

        if (altError) {
          console.error("Error in alternate token search:", altError);
          return { success: true, claimed: 0 };
        }

        if (!altTokens || altTokens.length === 0) {
          return { success: true, claimed: 0 };
        }

        // Process the tokens found with alternate search
        const tokenIds = altTokens.map(token => token.id);
        const totalTokens = altTokens.length;

        // Check which tokens have pre-created memorials
        const tokensWithMemorials = altTokens.filter(token => token.memorial_id);
        const hasPreCreatedMemorials = tokensWithMemorials.length > 0;

        // Collect all memorial IDs for transfer
        const memorialIdsToTransfer = new Set<string>();
        tokensWithMemorials.forEach(token => {
          if (token.memorial_id) memorialIdsToTransfer.add(token.memorial_id);
        });

        // Process each token separately for better error handling
        let successfulClaims = 0;

        for (const token of altTokens) {
          // First update token ownership
          const { error: updateError } = await supabase
            .from('memorial_tokens')
            .update({
              claimed: true,
              claimed_by: userId,
              email: normalizedEmail
            })
            .eq('id', token.id);

          if (updateError) {
            console.error(`Error claiming token ${token.id}:`, updateError);
          } else {
            successfulClaims++;
          }
        }

        // Transfer all memorials in one operation
        if (memorialIdsToTransfer.size > 0) {
          const memorialIdsArray = Array.from(memorialIdsToTransfer);

          const { error: memorialUpdateError } = await supabase
            .from('memorials')
            .update({
              user_id: userId,
              is_active: true // Always activate memorial when claiming ownership
            })
            .in('id', memorialIdsArray);

          if (memorialUpdateError) {
            console.error(`Error updating memorial ownership:`, memorialUpdateError);
          }
        }

        // Verify tokens were actually updated
        const { data: verifiedTokens, error: verifyError } = await supabase
          .from('memorial_tokens')
          .select('id, claimed, claimed_by')
          .in('id', tokenIds);

        if (verifyError) {
          console.error("Error verifying token updates:", verifyError);
        } else {
          const claimedCount = verifiedTokens?.filter(t => t.claimed && t.claimed_by === userId).length || 0;

          // If verification shows fewer claimed than we thought, update our count
          if (claimedCount < successfulClaims) {
            successfulClaims = claimedCount;
          }
        }

        // Verify memorials were transferred by checking current ownership
        let memorialsTransferred = 0;
        if (memorialIdsToTransfer.size > 0) {
          const memorialIdsArray = Array.from(memorialIdsToTransfer);

          const { data: verifiedMemorials, error: verifyMemorialError } = await supabase
            .from('memorials')
            .select('id, user_id')
            .in('id', memorialIdsArray);

          if (verifyMemorialError) {
            console.error("Error verifying memorial transfers:", verifyMemorialError);
          } else {
            memorialsTransferred = verifiedMemorials?.filter(m => m.user_id === userId).length || 0;
          }
        }

        const finalQuantity = successfulClaims;

        return {
          success: true,
          claimed: finalQuantity,
          hasPreCreatedMemorials: hasPreCreatedMemorials && memorialsTransferred > 0
        };
      }
      
      // Process tokens found with exact email match
      const tokenIds = pendingTokens.map(token => token.id);
      const totalTokens = pendingTokens.length;

      // Check which tokens have pre-created memorials
      const tokensWithMemorials = pendingTokens.filter(token => token.memorial_id);
      const hasPreCreatedMemorials = tokensWithMemorials.length > 0;

      // Collect all memorial IDs for transfer
      const memorialIdsToTransfer = new Set<string>();
      tokensWithMemorials.forEach(token => {
        if (token.memorial_id) memorialIdsToTransfer.add(token.memorial_id);
      });

      // Process each token separately
      let successfulClaims = 0;

      for (const token of pendingTokens) {
        // First update token ownership
        const { error: updateError } = await supabase
          .from('memorial_tokens')
          .update({
            claimed: true,
            claimed_by: userId,
            email: normalizedEmail
          })
          .eq('id', token.id);

        if (updateError) {
          console.error(`Error claiming token ${token.id}:`, updateError);
        } else {
          successfulClaims++;
        }
      }

      // Transfer all memorials in one operation
      if (memorialIdsToTransfer.size > 0) {
        const memorialIdsArray = Array.from(memorialIdsToTransfer);

        const { error: memorialUpdateError } = await supabase
          .from('memorials')
          .update({
            user_id: userId,
            is_active: true // Always activate memorial when claiming ownership
          })
          .in('id', memorialIdsArray);

        if (memorialUpdateError) {
          console.error(`Error updating memorial ownership:`, memorialUpdateError);
        }
      }

      // Verify tokens were actually updated
      const { data: verifiedTokens, error: verifyError } = await supabase
        .from('memorial_tokens')
        .select('id, claimed, claimed_by')
        .in('id', tokenIds);

      if (verifyError) {
        console.error("Error verifying token updates:", verifyError);
      } else {
        const claimedCount = verifiedTokens?.filter(t => t.claimed && t.claimed_by === userId).length || 0;

        // If verification shows fewer claimed than we thought, update our count
        if (claimedCount < successfulClaims) {
          successfulClaims = claimedCount;
        }
      }

      // Verify memorials were transferred by checking current ownership
      let memorialsTransferred = 0;
      if (memorialIdsToTransfer.size > 0) {
        const memorialIdsArray = Array.from(memorialIdsToTransfer);

        const { data: verifiedMemorials, error: verifyMemorialError } = await supabase
          .from('memorials')
          .select('id, user_id')
          .in('id', memorialIdsArray);

        if (verifyMemorialError) {
          console.error("Error verifying memorial transfers:", verifyMemorialError);
        } else {
          memorialsTransferred = verifiedMemorials?.filter(m => m.user_id === userId).length || 0;
        }
      }

      const finalQuantity = successfulClaims;

      return {
        success: true,
        claimed: finalQuantity,
        hasPreCreatedMemorials: hasPreCreatedMemorials && memorialsTransferred > 0
      };
    } catch (error) {
      console.error("Unexpected error in claimPendingTokensByEmail:", error);
      return { success: false, claimed: 0 };
    } finally {
      isClaimingRef.current = false;
      setIsProcessing(false);
    }
  }, []);

  const claimPendingTokensByPhone = useCallback(async (phone: string, userId: string): Promise<{ success: boolean; claimed: number; hasPreCreatedMemorials?: boolean }> => {
    try {
      if (!phone || !userId) {
        return { success: false, claimed: 0 };
      }

      // Check for B2B tokens by assigned_phone
      const { data: b2bTokens, error: b2bError } = await supabase
        .from('memorial_tokens')
        .select('id, memorial_id, assigned_phone')
        .eq('token_type', 'b2b')
        .eq('assigned_phone', phone)
        .eq('claimed', false);

      if (b2bError) {
        console.error("Error fetching B2B tokens by phone:", b2bError);
        return { success: false, claimed: 0 };
      }

      if (!b2bTokens || b2bTokens.length === 0) {
        return { success: true, claimed: 0 };
      }

      // Process B2B tokens — same logic as claimPendingTokensByEmail
      for (const token of b2bTokens) {
        const { error: updateError } = await supabase
          .from('memorial_tokens')
          .update({ claimed: true, claimed_by: userId })
          .eq('id', token.id);

        if (updateError) {
          console.error(`Error claiming B2B token ${token.id}:`, updateError);
        }

        if (token.memorial_id) {
          const { error: memorialError } = await supabase
            .from('memorials')
            .update({ user_id: userId, is_active: true })
            .eq('id', token.memorial_id);

          if (memorialError) {
            console.error(`Error transferring B2B memorial ${token.memorial_id}:`, memorialError);
          }
        }
      }

      // Verify transfers
      const b2bMemorialIds = b2bTokens.filter(t => t.memorial_id).map(t => t.memorial_id!);
      if (b2bMemorialIds.length > 0) {
        const { data: verified } = await supabase
          .from('memorials')
          .select('id, user_id')
          .in('id', b2bMemorialIds);

        const transferred = verified?.filter(m => m.user_id === userId).length || 0;

        return {
          success: true,
          claimed: b2bTokens.length,
          hasPreCreatedMemorials: transferred > 0
        };
      }

      return { success: true, claimed: b2bTokens.length };
    } catch (error) {
      console.error("Unexpected error in claimPendingTokensByPhone:", error);
      return { success: false, claimed: 0 };
    }
  }, []);

  const getAvailableMemorialCount = useCallback(async (userId: string): Promise<number> => {
    try {
      // Get count of claimed tokens for this user
      const { count: tokenCount, error: tokensError } = await supabase
        .from('memorial_tokens')
        .select('id', { count: 'exact', head: true })
        .eq('claimed_by', userId)
        .eq('claimed', true);

      if (tokensError) {
        console.error("Error fetching token count:", tokensError);
        return 0;
      }

      // Count existing memorials
      const { count: memorialCount, error: memorialsError } = await supabase
        .from('memorials')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (memorialsError) {
        console.error("Error counting existing memorials:", memorialsError);
        return 0;
      }

      const totalTokens = tokenCount || 0;
      const usedCount = memorialCount || 0;
      const availableCount = Math.max(0, totalTokens - usedCount);

      return availableCount;
    } catch (error) {
      console.error("Error calculating available memorial count:", error);
      return 0;
    }
  }, []);

  return {
    redeemToken,
    claimPendingTokensByEmail,
    claimPendingTokensByPhone,
    getAvailableMemorialCount,
    isProcessing
  };
}
